import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { MessageSquare, History } from 'lucide-react';
import { toast } from 'sonner';

export function CommentsSection({ entityId, getComments, addComment, currentUser, label = 'Comments', placeholder }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchComments = useCallback(async () => {
    if (!entityId) return;
    setLoading(true);
    try {
      const res = await getComments(entityId);
      setComments(res.data);
    } catch (err) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [entityId, getComments]);

  useEffect(() => { fetchComments(); }, [fetchComments]);

  const handleSubmit = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await addComment(entityId, newComment.trim());
      setNewComment('');
      fetchComments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  if (!entityId) return null;

  return (
    <div data-testid="comments-section">
      <Label className="flex items-center gap-1.5">
        <MessageSquare size={14} /> {label}{comments.length > 0 && <span className="text-[#999999] font-normal"> ({comments.length})</span>}
      </Label>
      <div className="mt-2 space-y-2 max-h-[240px] overflow-y-auto pr-1">
        {loading && <p className="text-xs text-[#999999]">Loading...</p>}
        {!loading && comments.length === 0 && (
          <p className="text-xs text-[#999999]">Nothing here yet.</p>
        )}
        {comments.map(c => c.is_activity ? (
          <div key={c.id} className="flex items-start gap-2 text-[11px] text-[#999999] px-1 py-1" data-testid={`activity-${c.id}`}>
            <History size={12} className="shrink-0 mt-0.5 text-[#BBBBBB]" />
            <span className="leading-snug">
              <span className="font-medium text-[#666666]">{c.user_name}</span> {c.content}
              <span className="text-[#BBBBBB]"> · {new Date(c.created_at).toLocaleString()}</span>
            </span>
          </div>
        ) : (
          <div key={c.id} className="bg-[#F5F5F5] rounded p-2" data-testid={`comment-${c.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 rounded-full bg-[#FFFF66] text-[#101010] flex items-center justify-center text-[10px] font-bold">
                {c.user_initials}
              </div>
              <span className="text-xs font-medium text-[#111111]">{c.user_name}</span>
              <span className="text-[10px] text-[#999999]">{new Date(c.created_at).toLocaleString()}</span>
            </div>
            <p className="text-sm text-[#111111] whitespace-pre-wrap pl-8">{c.content}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-2">
        <Textarea
          value={newComment}
          onChange={e => setNewComment(e.target.value)}
          placeholder={placeholder || `Add a comment as ${currentUser?.name || 'you'}...`}
          rows={2}
          data-testid="comment-input"
        />
        <Button onClick={handleSubmit} disabled={submitting || !newComment.trim()} className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white self-end" data-testid="comment-submit-btn">
          Post
        </Button>
      </div>
    </div>
  );
}
